/* src/App.js */

import React, { Suspense, lazy, useEffect } from "react";
import { Routes, Route, Outlet } from "react-router-dom";
import { FaSpinner } from "react-icons/fa";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import Header from "./components/Header"; 
import Footer from "./components/Footer";
import ErrorBoundary from "./components/ErrorBoundary";
import AdminLayout from "./components/admin/AdminLayout";
import ScrollToTop from "./components/ScrollToTop";
import "./App.css";
import '@fortawesome/fontawesome-free/css/all.min.css';

// Direct imports for main pages (no lazy loading for better performance)
import Home from "./pages/Home";
import About from "./pages/About";
import Styling from "./pages/Styling";
import ContactUs from "./pages/ContactUs";
import Services from "./pages/Services";
import ITConsulting from "./pages/ITConsulting";
import ITHealthCheck from "./pages/ITHealthCheck";
import ComputerRepair from "./pages/ComputerRepair";
import ManagedITServices from "./pages/ManagedITServices";
import CloudServices from "./pages/CloudServices";
import CyberSecurity from "./pages/CyberSecurity";
import DataBackupRecovery from "./pages/DataBackupRecovery";
import AccountingITServices from "./pages/AccountingITServices";
import BackupReadinessReview from "./pages/BackupReadinessReview";
import ComputerRepairShreveport from "./pages/ComputerRepairShreveport";
import ContractorITServices from "./pages/ContractorITServices";
import DentalITServices from "./pages/DentalITServices";
import HowToChooseIT from "./pages/HowToChooseIT";
import LawFirmITServices from "./pages/LawFirmITServices";
import ManagedITShreveport from "./pages/ManagedITShreveport";
import ManagedVsBreakFix from "./pages/ManagedVsBreakFix";
import MedicalOfficeIT from "./pages/MedicalOfficeIT";
import RansomwareProtection from "./pages/RansomwareProtection";
import RestaurantITServices from "./pages/RestaurantITServices";
import NotFound from "./pages/NotFound";
import Unauthorized from "./pages/Unauthorized";

// Lazy-loaded components (auth and admin only)
function lazyWithRetry(importFunc, retries = 3, delay = 1000) {
  return lazy(() =>
    new Promise((resolve, reject) => {
      function attempt() {
        importFunc()
          .then(resolve)
          .catch((error) => {
            if (retries === 0) {
              reject(error);
            } else {
              setTimeout(() => {
                retries--;
                attempt();
              }, delay);
            }
          });
      }
      attempt();
    })
  );
}

// Auth components
const Login = lazyWithRetry(() => import(/* webpackChunkName: "Login", webpackPrefetch: true */ "./components/auth/Login"));
const LoginSuccess = lazy(() => import("./pages/LoginSuccess"));
const Profile = lazy(() => import("./pages/Profile"));

// Admin Pages (Lazy Loaded)
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const ContactSubmissions = lazy(() => import("./pages/admin/ContactSubmissions"));
const AdminOrganization = lazy(() => import("./pages/admin/AdminOrganization"));
const AdminLocation = lazy(() => import("./pages/admin/AdminLocation"));
const AdminDevices = lazy(() => import("./pages/admin/AdminDevices"));
const AdminServices = lazy(() => import("./pages/admin/AdminServices"));
const AdminBilling = lazy(() => import("./pages/admin/AdminBilling")); 
const AdminAnalytics = lazy(() => import("./pages/admin/AdminAnalytics"));
const AdminSystem = lazy(() => import("./pages/admin/AdminSystem"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));

const ApiPlayground = lazy(() => import("./pages/api-playground/api-playground.tsx")); 
const ApiDocsPage = lazy(() => import("./pages/api-docs/page.tsx"));

// Loading component
const PageLoader = () => (
  <div className="page-loader">
    <div className="loader-container">
      <FaSpinner className="spinner-icon" aria-hidden="true" />
      <p>Loading...</p>
    </div>
  </div>
);

// Public layout wrapper - renders Header/Footer once for all public routes
const PublicLayout = () => (
  <>
    <Header />
    <main className="main-content" role="main">
      <Outlet />
    </main>
    <Footer />
  </>
);

function App() {
  useEffect(() => {
    // Initialize Google Analytics in production
    if (process.env.NODE_ENV === 'production') {
      window.dataLayer = window.dataLayer || [];
      function gtag() {
        window.dataLayer.push(arguments);
      }
      gtag('js', new Date());
      gtag('config', 'G-MEASUREMENT-ID');
    }
  }, []);

  return (
    <AuthProvider>
      <HelmetProvider>
      <div className="App">
        <ScrollToTop />
        <ErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public routes wrapped in PublicLayout */}
              <Route element={<PublicLayout />}>
                <Route path="/" element={<Home />} />
                <Route path="/about" element={<About />} />
                <Route path="/styling" element={<Styling />} />
                <Route path="/contact-us" element={<ContactUs />} />
                
                {/* Service pages */}
                <Route path="/services" element={<Services />} />
                <Route path="/it-health-check" element={<ITHealthCheck />} />
                <Route path="/it-consulting" element={<ITConsulting />} />
                <Route path="/computer-repair" element={<ComputerRepair />} />
                <Route path="/managed-it-services" element={<ManagedITServices />} />
                <Route path="/cloud-services" element={<CloudServices />} />
                <Route path="/cybersecurity" element={<CyberSecurity />} />
                <Route path="/data-backup-recovery" element={<DataBackupRecovery />} />

                {/* Local SEO and industry pages */}
                <Route path="/computer-repair-shreveport" element={<ComputerRepairShreveport />} />
                <Route path="/managed-it-services-shreveport" element={<ManagedITShreveport />} />
                <Route path="/it-support-accounting-firms" element={<AccountingITServices />} />
                <Route path="/it-support-contractors" element={<ContractorITServices />} />
                <Route path="/it-support-dental-offices" element={<DentalITServices />} />
                <Route path="/it-support-law-firms" element={<LawFirmITServices />} />
                <Route path="/it-support-medical-offices" element={<MedicalOfficeIT />} />
                <Route path="/it-support-restaurants" element={<RestaurantITServices />} />
                <Route path="/how-to-choose-it-support" element={<HowToChooseIT />} />
                <Route path="/managed-it-vs-break-fix" element={<ManagedVsBreakFix />} />
                <Route path="/ransomware-protection" element={<RansomwareProtection />} />
                <Route path="/backup-readiness-review" element={<BackupReadinessReview />} />
                
                {/* Auth routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/login-success" element={<LoginSuccess />} />
                <Route path="/unauthorized" element={<Unauthorized />} />
                
                {/* API Playground */}
                <Route path="/api-playground" element={<ApiPlayground />} />
                
                {/* 404 */}
                <Route path="*" element={<NotFound />} />
              </Route>

              {/* Protected user routes with PublicLayout */}
              <Route element={<PublicLayout />}>
                <Route 
                  path="/profile" 
                  element={
                    <ProtectedRoute>
                      <Profile />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/api-docs" 
                  element={
                    <ProtectedRoute>
                      <ApiDocsPage />
                    </ProtectedRoute>
                  } 
                />
              </Route>
              
              {/* Admin Routes - Wrapped in AdminLayout */}
              <Route 
                path="/dashboard" 
                element={
                  <ProtectedRoute requireAdmin={true}>
                    <AdminLayout /> 
                  </ProtectedRoute>
                }
              >
                <Route index element={<AdminDashboard />} /> 
                <Route path="home" element={<AdminDashboard />} /> 
                <Route path="users" element={<AdminUsers />} />
                <Route path="contacts" element={<ContactSubmissions />} />
                <Route path="organization" element={<AdminOrganization />} />
                <Route path="location" element={<AdminLocation />} />
                <Route path="devices" element={<AdminDevices />} />
                <Route path="services" element={<AdminServices />} />
                <Route path="billing" element={<AdminBilling />} />
                <Route path="analytics" element={<AdminAnalytics />} />
                <Route path="system" element={<AdminSystem />} />
                <Route path="settings" element={<AdminSettings />} />
              </Route>
            </Routes> 
          </Suspense>
        </ErrorBoundary>
      </div>
      </HelmetProvider>
    </AuthProvider>
  );
}

export default App;
